/**
 * ⓘ card for "What guides our analysis": how to read the driver shares, what
 * each confidence tier means (mirrors insights-api `_confidence`), and a
 * definition for each driver this site actually shows.
 */
import type React from "react"
import { useTranslation } from "react-i18next"

import { Box, Stack, Typography } from "@mui/material"

import { SHAP_TERM_KEYS } from "./e2-info-content"
import { InfoPopover } from "./info-popover"

const TIERS = ["high", "medium", "low"] as const

export const WhatGuidesInfo: React.FC<{
  drivers?: { label: string; share_pct: number }[]
}> = ({ drivers }) => {
  const { t } = useTranslation("common")
  const terms = (drivers ?? []).flatMap((d) => {
    const key = SHAP_TERM_KEYS[d.label]
    return key ? [key] : []
  })

  return (
    <InfoPopover
      label={t("neat.e2.info.whatGuides.open")}
      title={t("neat.e2.info.whatGuides.title")}
    >
      <Typography variant="body2">
        {t("neat.e2.info.whatGuides.howToRead")}
      </Typography>

      <Box>
        <Typography sx={{ fontWeight: "bold" }} variant="body2">
          {t("neat.e2.info.whatGuides.confidenceTitle")}
        </Typography>
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 3 }}>
          {TIERS.map((tier) => (
            <Typography component="li" key={tier} variant="body2">
              {t(`neat.e2.info.whatGuides.confidence.${tier}`)}
            </Typography>
          ))}
        </Stack>
      </Box>

      {terms.length > 0 ? (
        <Box>
          <Typography sx={{ fontWeight: "bold" }} variant="body2">
            {t("neat.e2.info.whatGuides.termsTitle")}
          </Typography>
          <Stack component="dl" spacing={0.75} sx={{ m: 0 }}>
            {terms.map((key) => (
              <Box key={key}>
                <Typography
                  component="dt"
                  sx={{ fontWeight: "medium" }}
                  variant="body2"
                >
                  {t(`neat.e2.info.whatGuides.names.${key}`)}
                </Typography>
                <Typography
                  component="dd"
                  sx={{ color: "text.secondary", m: 0 }}
                  variant="body2"
                >
                  {t(`neat.e2.info.whatGuides.terms.${key}`)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </InfoPopover>
  )
}
